import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ANALYSIS_QUERY_KEYS } from '@/entities/analysis';
import { PERSONA_QUERY_KEYS } from '@/entities/persona';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { saveAudienceDraft } from '@/features/analysis/api/analysis.api';
import type { AudienceDraftEnvelope } from '@/features/analysis/api/analysis.api';
import { notificationService } from '@/lib/services/notification.service';
import { track } from '@/shared/analytics';

/**
 * Puts one of a report's audiences in the Knowledge Hub. The draft says which block it came
 * from, since that is what decides the route.
 *
 * Two things are re-read afterwards: the report, which is what records the audience as saved
 * and stops offering to save it again, and the workspace's audience list, which now has one
 * more entry.
 */
export const useSaveAudienceDraft = (analysisId: string | undefined) => {
  const { t } = useTranslation('analysis');
  const queryClient = useQueryClient();
  const workspaceId = useCurrentWorkspaceId();

  return useMutation({
    mutationFn: ({
      draftId,
      envelope,
    }: {
      draftId: string;
      envelope: AudienceDraftEnvelope;
      segmentName?: string;
    }) => saveAudienceDraft(analysisId!, envelope, draftId),
    onSuccess: (result, { envelope, segmentName }) => {
      track('iq_suggested_audience_saved', {
        analysis_id: analysisId ?? '',
        persona_id: result.personaId,
        // Both kinds of audience reach here and the event name only names one of them.
        source: envelope === 'uploadedAudiences' ? 'uploaded' : 'suggested',
      });
      notificationService.showSuccess(
        t('analysisResult.suggestedAudiences.audienceSavedToKnowledge', {
          name: segmentName ?? t('analysisResult.suggestedAudiences.thisAudience'),
        }),
      );

      void queryClient.invalidateQueries({
        queryKey: ANALYSIS_QUERY_KEYS.detail(analysisId!),
      });
      if (workspaceId) {
        void queryClient.invalidateQueries({ queryKey: PERSONA_QUERY_KEYS.list(workspaceId) });
      }
    },
    onError: () => {
      notificationService.showError(t('analysisResult.suggestedAudiences.couldNotSaveThe'));
    },
  });
};
